import { AnnotatedType, PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { select, t, Trans } from '@lingui/macro';
import { useState } from 'react';

type ClimateActionChapter4Props = {
  data: ClimateActionData['strong_govt_action'];
};
export function ClimateActionChapter4(props: ClimateActionChapter4Props) {
  const { data } = props;

  const formatValue = useNumberFormatting('percent');

  const getLabel = (key: string) =>
    t({
      id: `climate-action.chapter-4.chart.bar-labels`,
      message: select(key, {
        lilmc: 'Low/Lower-middle income countries',
        umc: 'Upper-middle income countries',
        hic: 'High-income countries',
      }),
    });

  const chartData = Object.entries(data).map(([key, value]) => ({
    label: getLabel(key),
    value,
  }));

  const [chartVisible, setChartVisible] = useState(false);

  return (
    <ScrollRoot>
      <ScrollGraphic>
        <HorizontalBarChart
          label={t({
            id: 'climate-action.chapter-4.chart-label',
            message: '% of young people who support bold government action on climate change',
          })}
          data={chartData}
          formatValue={formatValue}
          visible={chartVisible}
        />
      </ScrollGraphic>

      <ScrollSteps offset={0.7} onStepEnter={() => setChartVisible(true)}>
        <ScrollStep>
          <StoryText>
            <Trans id="climate-action.chapter-4.text-1">
              We found the strongest support among young people for government action on climate
              change in <PrimaryAnnotatedType>poorer countries.</PrimaryAnnotatedType>
            </Trans>
          </StoryText>
        </ScrollStep>

        <ScrollStep>
          <StoryText>
            <Trans id="climate-action.chapter-4.text-2">
              That’s telling, as these countries are least equipped to respond to the climate
              crisis, and yet are <AnnotatedType>most exposed</AnnotatedType> to its effects.
            </Trans>
          </StoryText>
        </ScrollStep>
      </ScrollSteps>
    </ScrollRoot>
  );
}
